import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useDataUploadsActions,
  useDynamicFormsFetch,
  useModalConfirm,
} from "@ryde/redux";
import {
  ConfirmResult,
  errors,
  EtlJobLogStatusEnum,
  IDataUploadDto,
  Log,
  useHasPermissions,
  warnAndToastSimpleResultWithFallback,
} from "@ryde/shared";

import { MuiButton } from "../../controls";
import { IRenderColumnTemplate } from "./_core";
import { DataUploadRunButtonStyled } from "./data-upload-run-button-cell.styled";

export const DataUploadRunButtonCellTemplate: FC<IRenderColumnTemplate> = ({
  rowOriginal,
}) => {
  const dataUpload = rowOriginal as IDataUploadDto;
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { executeDataUpload } = useDataUploadsActions();
  const { fetchTableRows } = useDynamicFormsFetch("dataUpload");
  const { hasPermissions: hasExecutePermission } = useHasPermissions({
    entity: "dataUpload",
    action: "execute",
  });
  // Per row, so one queued load does not disable the buttons of the other rows.
  const [isQueueing, setIsQueueing] = useState(false);

  const isRunning =
    dataUpload.metadata?.lastRunStatus === EtlJobLogStatusEnum.Running;

  const handleRunClick = useEvent(async (): Promise<void> => {
    setIsQueueing(true);

    try {
      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t("dataUpload.confirmRunNow", { name: dataUpload.name }),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        return;
      }

      const result = await executeDataUpload(dataUpload.id);

      if (!result.success) {
        // The backend's own key first — "this load is no longer available" reads better than a generic
        // failure when an admin has just closed the schedule; the local key is only the fallback.
        warnAndToastSimpleResultWithFallback(
          result,
          t,
          errors.dataUpload.failedToQueueRun,
        );
        return;
      }

      Log.successAndToast(t("dataUpload.runQueuedSuccessfully"));

      fetchTableRows({ isPreloader: false });
    } finally {
      // In a `finally` because the confirm dialog renders caller-supplied content and can throw:
      // without it a single failed render would leave this row's button dead until the page reloads.
      setIsQueueing(false);
    }
  });

  if (!hasExecutePermission) {
    return null;
  }

  return (
    <DataUploadRunButtonStyled>
      <MuiButton
        muiVariant="outlined"
        muiSize="small"
        muiStyle="primary"
        e2eData="data-upload-run-button"
        disabled={isQueueing || isRunning}
        onClick={handleRunClick}
      >
        {t("dataUpload.runNow")}
      </MuiButton>
    </DataUploadRunButtonStyled>
  );
};
